import { axios } from '@store/axios';

/**
 * Uploads for the canvas editor: images (image blocks + post covers) and
 * PDF documents (Document palette entry → Button block).
 * Extracted from PageEditor4's Quill image handler — same endpoint
 * (`POST /organizations/d/{leagueName}/imageupload`, FormData field `upload`,
 * response `result.fileUrl`). Images keep the client-side resize (max width
 * 1330px, quality 0.9) and 5MB cap; PDFs skip the resize (it would mangle
 * them) and cap at 15MB (matching the server limit). The server allows only
 * image/* and application/pdf.
 */

const baseUrl = `${process.env.API_URL}/api/v1`;
const MAX_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_PDF_SIZE_BYTES = 15 * 1024 * 1024;
const MAX_WIDTH = 1330;

export class UploadError extends Error {}

function resizeImage(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_WIDTH) {
          height = Math.round((height * MAX_WIDTH) / width);
          width = MAX_WIDTH;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new UploadError('Canvas unavailable'));
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new UploadError('Could not process image'));
              return;
            }
            resolve(new File([blob], file.name, { type: file.type, lastModified: Date.now() }));
          },
          file.type,
          0.9
        );
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
}

async function postUpload(
  file: File,
  leagueName: string,
  getToken: () => Promise<string | null>
): Promise<string> {
  const formData = new FormData();
  formData.append('upload', file);
  const token = await getToken();
  const { data } = await axios.post(
    `${baseUrl}/organizations/d/${leagueName}/imageupload`,
    formData,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const fileUrl = data?.result?.fileUrl;
  if (!fileUrl) {
    throw new UploadError(data?.error || 'Upload failed');
  }
  return fileUrl;
}

export async function uploadImage(
  file: File,
  leagueName: string,
  // clerkShim's getToken is typed Promise<string | null>; the API rejects a
  // missing token with its own 401 — no need to pre-check here.
  getToken: () => Promise<string | null>
): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new UploadError('Please select an image file');
  }
  if (file.size > MAX_SIZE_BYTES) {
    throw new UploadError('File size must be less than 5MB');
  }

  const resized = await resizeImage(file);
  return postUpload(resized, leagueName, getToken);
}

// PDF documents (league rules, forms, waivers) published on the site. No
// resize pass — the canvas pipeline would corrupt a non-image.
export async function uploadFile(
  file: File,
  leagueName: string,
  getToken: () => Promise<string | null>
): Promise<string> {
  if (file.type !== 'application/pdf') {
    throw new UploadError('Please select a PDF file');
  }
  if (file.size > MAX_PDF_SIZE_BYTES) {
    throw new UploadError('File size must be less than 15MB');
  }
  return postUpload(file, leagueName, getToken);
}
